import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { getDevice } from "../../lib/api";

// Per-device mock FW history and app log
const DEVICE_HISTORY = {
  "T-2041": {
    segment: "Canary",
    history: [
      { version: "v1.5.0", date: "2026-04-18 14:07", outcome: "SUCCESS" },
      { version: "v1.4.2", date: "2026-02-05 09:30", outcome: "SUCCESS" },
      { version: "v1.4.1", date: "2025-12-10 16:14", outcome: "ROLLBACK" },
      { version: "v1.4.0", date: "2025-10-22 11:05", outcome: "SUCCESS" },
    ],
    appLog: [
      { ts: "2026-04-18 14:07", actor: "CLIN-07", event: "TRIGGER_UPDATE\n\u2192 HASH_OK \u2192\nReboot",           fw: "v1.5.0", outcome: "SUCCESS" },
      { ts: "2026-04-18 13:58", actor: "CLIN-07", event: "START_UPDATE_SESSION\n\u00b7 WRITE_CHUNK \u00d7512",     fw: "v1.5.0", outcome: "TRANSFER" },
      { ts: "2026-04-18 13:55", actor: "CLIN-07", event: "GET\n/firmware/available\n\u00b7 v1.4.2\u2192v1.5.0",   fw: "v1.5.0", outcome: "AVAILABLE" },
      { ts: "2025-12-10 16:14", actor: "TECH-02", event: "HASH_FAIL\n\u2192\nROLLBACK\ninitiated",                fw: "v1.4.1", outcome: "ROLLBACK" },
      { ts: "2025-12-10 15:50", actor: "TECH-02", event: "START_UPDATE_SESSION\n\u00b7 WRITE_CHUNK \u00d7512",     fw: "v1.4.1", outcome: "FAIL" },
    ],
  },
  "T-2039": {
    segment: "All Clinicians",
    history: [
      { version: "v1.4.2", date: "2026-01-14 09:22", outcome: "SUCCESS" },
      { version: "v1.4.0", date: "2025-09-05 10:00", outcome: "SUCCESS" },
    ],
    appLog: [
      { ts: "2026-01-14 09:22", actor: "CLIN-03", event: "TRIGGER_UPDATE\n\u2192 HASH_OK",   fw: "v1.4.2", outcome: "SUCCESS" },
      { ts: "2026-01-14 09:10", actor: "CLIN-03", event: "START_UPDATE_SESSION\n\u00b7 WRITE_CHUNK \u00d7512", fw: "v1.4.2", outcome: "TRANSFER" },
    ],
  },
};

const DEFAULT_HISTORY = {
  segment: "All Clinicians",
  history: [{ version: "v1.5.0", date: "2026-04-20 10:15", outcome: "SUCCESS" }],
  appLog: [{ ts: "2026-04-20 10:15", actor: "CLIN-01", event: "TRIGGER_UPDATE\n\u2192 HASH_OK", fw: "v1.5.0", outcome: "SUCCESS" }],
};

const OUTCOME_STYLE = {
  SUCCESS:   "text-emerald-600 font-bold",
  TRANSFER:  "text-blue-500 font-bold",
  AVAILABLE: "text-blue-500 font-bold",
  ROLLBACK:  "text-orange-500 font-bold",
  FAIL:      "text-red-500 font-bold",
};

const OUTCOME_SHORT = {
  SUCCESS:   "SUCCESS",
  TRANSFER:  "TRA...",
  AVAILABLE: "AVAIL",
  ROLLBACK:  "ROLLBACK",
  FAIL:      "FAIL",
};

const DOT_COLOR = {
  SUCCESS:  "bg-emerald-500",
  ROLLBACK: "bg-red-500",
};

export default function DeviceDetailPage() {
  const { serial } = useParams();
  const navigate = useNavigate();
  const [device, setDevice] = useState(null);
  const [tab, setTab] = useState("applog");

  useEffect(() => { getDevice(serial).then(setDevice).catch(console.error); }, [serial]);

  if (!device) return <div className="p-8 text-center text-slate-400">Loading...</div>;

  const info = DEVICE_HISTORY[serial] || DEFAULT_HISTORY;

  const statusCfg = {
    UP_TO_DATE:      { label: "Up to Date",       pill: "border border-emerald-400 text-emerald-600 bg-emerald-50" },
    MANDATORY:       { label: "Mandatory",         pill: "border border-red-300 text-red-600 bg-red-50" },
    PENDING:         { label: "Pending",           pill: "border border-amber-300 text-amber-600 bg-amber-50" },
    FAILED_ROLLBACK: { label: "Failed - Rollback", pill: "border border-red-300 text-red-600 bg-red-50" },
  }[device.status] || { label: device.status, pill: "border border-slate-300 text-slate-500" };

  return (
    <div className="flex flex-col gap-0">
      {/* Page title row */}
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-start gap-4">
          <button
            onClick={() => navigate("/devices")}
            className="flex items-center gap-1.5 text-sm font-medium text-slate-500 hover:text-slate-800 border border-slate-200 rounded-lg px-3 py-2 bg-white transition-colors shrink-0 mt-1"
          >
            &#8592; Fleet Overview
          </button>
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Device {serial}</h1>
            <p className="text-sm text-slate-400 mt-0.5">
              {device.model} &middot; South Wing Clinic &middot; Patient: [de-identified]
            </p>
          </div>
        </div>
        <span className={`text-sm font-semibold px-4 py-1.5 rounded-full mt-1 ${statusCfg.pill}`}>
          {statusCfg.label}
        </span>
      </div>

      {/* Dark teal header bar */}
      <div className="bg-[hsl(196,64%,24%)] rounded-xl px-6 py-4 mb-5">
        <div className="grid grid-cols-4 gap-6">
          {[
            { label: "SERIAL NO.",   value: serial,             bold: true },
            { label: "CURRENT FW",  value: device.currentFw,   teal: true },
            { label: "LAST UPDATED", value: device.lastUpdated?.slice(0, 10) || "-" },
            { label: "SEGMENT",      value: info.segment },
          ].map(({ label, value, bold, teal }) => (
            <div key={label}>
              <p className="text-[11px] font-semibold text-teal-300 uppercase tracking-wider mb-1">{label}</p>
              <p className={`text-base font-bold ${teal ? "text-teal-300" : "text-white"}`}>{value}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Body: two-column */}
      <div className="flex gap-5">
        {/* Left: FW History */}
        <div className="w-52 shrink-0 bg-white border border-slate-200 rounded-xl px-4 py-5">
          <p className="text-[11px] font-bold text-teal-700 uppercase tracking-widest mb-4">FW History</p>
          <div className="relative">
            {/* Vertical line */}
            <div className="absolute left-[7px] top-3 bottom-3 w-px bg-slate-200" />
            <div className="space-y-5">
              {info.history.map((h, i) => (
                <div key={i} className="flex gap-3 items-start relative">
                  <div className={`w-3.5 h-3.5 rounded-full mt-0.5 shrink-0 z-10 ${DOT_COLOR[h.outcome] || "bg-slate-300"}`} />
                  <div>
                    <p className="text-sm font-bold text-slate-800">{h.version}</p>
                    <p className="text-xs text-slate-400 mb-1">{h.date}</p>
                    <span className={`text-[11px] font-semibold px-2 py-0.5 rounded-full border ${
                      h.outcome === "SUCCESS"  ? "bg-emerald-50 text-emerald-600 border-emerald-200" :
                      h.outcome === "ROLLBACK" ? "bg-amber-50 text-amber-600 border-amber-200" :
                      "bg-slate-50 text-slate-500 border-slate-200"
                    }`}>{h.outcome}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Right: Tabs */}
        <div className="flex-1 bg-white border border-slate-200 rounded-xl overflow-hidden">
          {/* Tab bar */}
          <div className="flex border-b border-slate-100 px-5 pt-3 gap-6">
            <button
              onClick={() => setTab("applog")}
              className={`text-sm font-semibold pb-2.5 border-b-2 transition-colors ${
                tab === "applog" ? "border-teal-600 text-teal-700" : "border-transparent text-slate-400 hover:text-slate-600"
              }`}
            >
              App Log
            </button>
            <button
              onClick={() => setTab("audit")}
              className={`text-sm font-semibold pb-2.5 border-b-2 transition-colors ${
                tab === "audit" ? "border-teal-600 text-teal-700" : "border-transparent text-slate-400 hover:text-slate-600"
              }`}
            >
              Audit Trail &rarr;
            </button>
          </div>

          {/* Immutable banner */}
          <div className="flex items-center gap-2 mx-5 mt-4 mb-3 px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-lg text-xs text-slate-500">
            <span className="text-base">&#x1F512;</span>
            Immutable append-only log &middot; Read-only &middot; IEC 62304 &sect;8 compliant
          </div>

          {/* Log table */}
          <div className="px-5 pb-5">
            <table className="w-full text-xs">
              <thead>
                <tr className="border-b border-slate-100">
                  {["TIMESTAMP UTC", "ACTOR", "EVENT", "FW", "OUTCOME"].map((h) => (
                    <th key={h} className="text-left py-2 pr-4 text-[10px] font-semibold text-slate-400 uppercase tracking-wide">
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {info.appLog.map((row, i) => (
                  <tr key={i} className={`border-b border-slate-50 align-top ${i % 2 === 0 ? "" : "opacity-80"}`}>
                    <td className="py-3 pr-4 text-slate-400 whitespace-nowrap">{row.ts}</td>
                    <td className="py-3 pr-4 font-semibold text-slate-700">{row.actor}</td>
                    <td className="py-3 pr-4 text-slate-600 whitespace-pre-line leading-4">{row.event}</td>
                    <td className="py-3 pr-4 font-mono text-teal-600 whitespace-nowrap">{row.fw}</td>
                    <td className={`py-3 whitespace-nowrap ${OUTCOME_STYLE[row.outcome] || "text-slate-500"}`}>
                      {OUTCOME_SHORT[row.outcome] || row.outcome}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}